import { ArrowLeft, TrendingUp as TrendIcon, Zap } from "lucide-react";
import { Card } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/card";
import { Button } from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/button";
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from "@/projects/Admin_Bite_Balance/bite-balance/src/components/ui/tabs";
import { useNavigate } from "react-router-dom";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
} from "recharts";

const ActiveMinutesDetail = () => {
  const navigate = useNavigate();

  const weeklyData = [
    { day: "Mon", minutes: 45 },
    { day: "Tue", minutes: 52 },
    { day: "Wed", minutes: 38 },
    { day: "Thu", minutes: 48 },
    { day: "Fri", minutes: 55 },
    { day: "Sat", minutes: 42 },
    { day: "Sun", minutes: 45 },
  ];

  const monthlyData = [
    { week: "Week 1", minutes: 315 },
    { week: "Week 2", minutes: 340 },
    { week: "Week 3", minutes: 295 },
    { week: "Week 4", minutes: 325 },
  ];

  return (
    <div className="min-h-screen bg-background pb-24">
      <div className="mx-auto max-w-md">
        {/* Header */}
        <div className="flex items-center gap-3 border-b border-border/50 p-4">
          <Button
            variant="ghost"
            size="icon"
            onClick={() => navigate("/fitness")}
            className="rounded-xl"
          >
            <ArrowLeft className="h-5 w-5" />
          </Button>
          <div className="flex-1">
            <h1 className="text-xl font-bold text-foreground">
              Active Minutes
            </h1>
            <p className="text-xs text-muted-foreground">
              Your daily activity time
            </p>
          </div>
        </div>

        <div className="space-y-4 p-4">
          {/* Today's Summary */}
          <Card className="from-chart-2/5 to-chart-2/10 rounded-2xl border-0 bg-gradient-to-br p-5 shadow-sm">
            <div className="mb-4 flex items-center gap-4">
              <div className="bg-chart-2/20 flex h-14 w-14 items-center justify-center rounded-xl">
                <Zap className="text-chart-2 h-7 w-7" />
              </div>
              <div className="flex-1">
                <div className="text-3xl font-bold text-foreground">45</div>
                <p className="text-sm text-muted-foreground">minutes today</p>
              </div>
            </div>
            <div className="grid grid-cols-3 gap-3 border-t border-border/30 pt-3">
              <div className="text-center">
                <div className="mx-auto mb-1 h-3 w-3 rounded-full bg-yellow-500" />
                <div className="text-lg font-bold text-foreground">18</div>
                <div className="text-xs text-muted-foreground">Fat Burn</div>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-1 h-3 w-3 rounded-full bg-orange-500" />
                <div className="text-lg font-bold text-foreground">20</div>
                <div className="text-xs text-muted-foreground">Cardio</div>
              </div>
              <div className="text-center">
                <div className="mx-auto mb-1 h-3 w-3 rounded-full bg-red-500" />
                <div className="text-lg font-bold text-foreground">7</div>
                <div className="text-xs text-muted-foreground">Peak</div>
              </div>
            </div>
          </Card>

          {/* Info Card */}
          <Card className="rounded-2xl border-0 bg-muted/30 p-4 shadow-sm">
            <div className="flex gap-3">
              <TrendIcon className="text-chart-2 mt-0.5 h-5 w-5 flex-shrink-0" />
              <div>
                <h4 className="mb-1 text-sm font-semibold text-foreground">
                  About Active Minutes
                </h4>
                <p className="text-xs leading-relaxed text-muted-foreground">
                  Active minutes track time spent in moderate to vigorous
                  physical activity. Fitbit detects this through elevated heart
                  rate and movement intensity, helping you meet WHO's
                  recommended 150 minutes per week.
                </p>
              </div>
            </div>
          </Card>

          {/* Tabs for Time Periods */}
          <Tabs defaultValue="weekly" className="w-full">
            <TabsList className="mb-4 grid w-full grid-cols-2">
              <TabsTrigger value="weekly">Weekly</TabsTrigger>
              <TabsTrigger value="monthly">Monthly</TabsTrigger>
            </TabsList>

            <TabsContent value="weekly" className="space-y-4">
              <Card className="rounded-2xl border-0 p-5 shadow-sm">
                <h3 className="mb-4 font-semibold text-foreground">
                  This Week
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={weeklyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="day"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar
                        dataKey="minutes"
                        fill="hsl(var(--chart-2))"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </TabsContent>

            <TabsContent value="monthly" className="space-y-4">
              <Card className="rounded-2xl border-0 p-5 shadow-sm">
                <h3 className="mb-4 font-semibold text-foreground">
                  This Month
                </h3>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <BarChart data={monthlyData}>
                      <CartesianGrid
                        strokeDasharray="3 3"
                        stroke="hsl(var(--border))"
                        opacity={0.3}
                      />
                      <XAxis
                        dataKey="week"
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <YAxis
                        stroke="hsl(var(--muted-foreground))"
                        fontSize={12}
                      />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: "hsl(var(--card))",
                          border: "1px solid hsl(var(--border))",
                          borderRadius: "8px",
                        }}
                      />
                      <Bar
                        dataKey="minutes"
                        fill="hsl(var(--chart-2))"
                        radius={[8, 8, 0, 0]}
                      />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              </Card>
            </TabsContent>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default ActiveMinutesDetail;
